import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private titleService: Title, 
    private metaService: Meta, 
    private router: Router, 
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document
  ) {}

  init() {
    // Set initial fallback from environment
    this.metaService.updateTag({ name: 'description', content: environment.seoDescription });
    if ((environment as any).seoKeywords) {
      this.metaService.updateTag({ name: 'keywords', content: (environment as any).seoKeywords });
    }
    this.metaService.updateTag({ property: 'og:image', content: `${environment.baseUrl}/assets/og-cover.jpg` });
    
    // Inject JSON-LD dynamically
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": environment.title,
      "url": environment.baseUrl,
      "logo": `${environment.baseUrl}/assets/logo.png`
    });
    this.document.head.appendChild(script);
    
    // Listen to route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data)
    ).subscribe(data => {
      // Use setTimeout to ensure Angular's TitleStrategy has already updated the document title
      setTimeout(() => {
        const description = data['description'] || environment.seoDescription;
        const keywords = data['keywords'] || (environment as any).seoKeywords;
        const title = this.titleService.getTitle();
        const currentUrl = `${environment.baseUrl}${this.router.url}`;
        
        // Update standard meta
        this.metaService.updateTag({ name: 'description', content: description });
        if (keywords) {
          this.metaService.updateTag({ name: 'keywords', content: keywords });
        }
        
        // Update OpenGraph
        this.metaService.updateTag({ property: 'og:title', content: title });
        this.metaService.updateTag({ property: 'og:description', content: description });
        this.metaService.updateTag({ property: 'og:url', content: currentUrl });
        
        // Update Twitter
        this.metaService.updateTag({ name: 'twitter:title', content: title });
        this.metaService.updateTag({ name: 'twitter:description', content: description });
        
        this.updateCanonicalUrl(currentUrl);
      }, 0);
    });
  }

  private updateCanonicalUrl(url: string) {
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
